import { memo } from "react";
import { Link } from "@/lib/router-compat";

type Country = "RO" | "UK" | "DE" | string;

interface Props {
  country: Country;
}

const CONTENT: Record<string, {
  h1: string;
  intro: string;
  howTitle: string;
  how: string[];
  whyTitle: string;
  why: string;
  coverageTitle: string;
  coverage: string;
  faqTitle: string;
  faqs: { q: string; a: string }[];
}> = {
  RO: {
    h1: "Mașini second hand de vânzare în România",
    intro: "Cauți după marcă, model și oraș. Vezi pozele și istoricul afișat în anunț. Suni direct vânzătorul. Noi nu luăm comision. Găsești mașini de la dealeri verificați RISCO și de la persoane fizice.",
    howTitle: "Cum funcționează",
    how: [
      "Alegi marca, modelul și orașul. Poți restrânge rezultatele după preț.",
      "Deschizi anunțul. Verifici pozele și datele pe care le-a pus vânzătorul.",
      "Suni vânzătorul sau îi scrii prin formular.",
    ],
    whyTitle: "De ce RO Auto Market",
    why: "Verificăm manual firma dealerului la ANAF și în RISCO: CUI, cod CAEN și statut fiscal. Nu verificăm starea vehiculelor. Poți vedea cine vinde mașina înainte să iei legătura cu el.",
    coverageTitle: "Caută în orașul tău",
    coverage: "Selectezi orașul în filtre și vezi anunțurile disponibile acolo. Dacă te interesează o mașină, contactezi direct vânzătorul.",
    faqTitle: "Întrebări frecvente",
    faqs: [
      {
        q: "Cum cumpăr o mașină second hand pe RO Auto Market?",
        a: "Caută anunțul dorit folosind filtrele de marcă, model, preț și oraș, verifică fotografiile și istoricul, apoi contactează direct vânzătorul prin telefon sau formular. Platforma nu percepe comision de la cumpărător.",
      },
      {
        q: "Cum știu că un dealer este de încredere?",
        a: "Dealerii verificați sunt marcați cu o insignă dedicată. Verificăm manual firma dealerului la ANAF și în RISCO: CUI, cod CAEN și statut fiscal. Nu verificăm starea vehiculelor.",
      },
      {
        q: "Pot vinde ca persoană fizică pe RO Auto Market?",
        a: "Da. Pachetele Standard și Promovat sunt active 30 de zile. Pachetul Activ până la vânzare se reînnoiește lunar până când anunțul este marcat ca vândut.",
      },
      {
        q: "Sunt afișate mașini din import UK și Germania?",
        a: "Da. O parte dintre dealerii parteneri importă vehicule de la dealeri verificați din Marea Britanie și Germania, iar anunțurile respective sunt marcate cu țara de proveniență și istoricul de service disponibil.",
      },
    ],
  },
  UK: {
    h1: "Used cars for sale: verified dealers",
    intro:
      "Search by make, model and city. See the photos and available history. Contact the seller directly. We do not charge buyers a commission.",
    howTitle: "How it works",
    how: [
      "Search by make, model, price, year or location using the advanced filters.",
      "Compare offers from verified dealers and private sellers, with full history, photos and specs.",
      "Contact the seller directly by phone, WhatsApp or form. No intermediaries, no hidden fees.",
    ],
    whyTitle: "Why RO Auto Market",
    why:
      "We check dealer companies using CUI, CAEN, RISCO and ANAF data, not the condition of each vehicle. You can see who is selling before you get in touch.",
    coverageTitle: "UK coverage",
    coverage:
      "Listings available across London, Manchester, Birmingham, Leeds, Glasgow, Bristol and other UK cities, plus additional stock from Romania and Germany for dealers importing MOT-checked vehicles.",
    faqTitle: "Frequently asked questions",
    faqs: [
      {
        q: "How do I buy a used car on RO Auto Market?",
        a: "Search with the make, model, price and city filters, review photos and history, then contact the seller directly by phone or form. The platform charges no buyer commission.",
      },
      {
        q: "How do I know a dealer is trustworthy?",
        a: "Verified dealers carry a dedicated marker. We check the dealer company using CUI, CAEN, RISCO and ANAF data, not the condition of each vehicle.",
      },
      {
        q: "Can I sell as a private seller?",
        a: "Yes. Standard and Promoted are active for 30 days. Until Sold renews monthly until the listing is marked as sold.",
      },
      {
        q: "Are imported vehicles from Romania and Germany listed?",
        a: "Yes. Some partner dealers import checked vehicles from Romania and Germany. Those listings are tagged with the country of origin and available service history.",
      },
    ],
  },
  DE: {
    h1: "Gebrauchtwagen von geprüften Händlern",
    intro:
      "Suchen Sie nach Marke, Modell und Stadt. Sehen Sie Fotos und verfügbare Fahrzeugdaten an. Kontaktieren Sie den Verkäufer direkt. Für Käufer fällt keine Provision an.",
    howTitle: "So funktioniert es",
    how: [
      "Suchen Sie nach Marke, Modell, Preis, Baujahr oder Standort mit den erweiterten Filtern.",
      "Vergleichen Sie Angebote von geprüften Händlern und Privatverkäufern mit voller Historie, Fotos und Ausstattung.",
      "Kontaktieren Sie den Verkäufer direkt per Telefon, WhatsApp oder Formular. Ohne Vermittler, ohne versteckte Gebühren.",
    ],
    whyTitle: "Warum RO Auto Market",
    why:
      "Wir prüfen Händlerfirmen anhand von CUI-, CAEN-, RISCO- und ANAF-Daten, nicht den Zustand jedes Fahrzeugs. Sie sehen, wer verkauft, bevor Sie Kontakt aufnehmen.",
    coverageTitle: "Abdeckung",
    coverage:
      "Sie können verfügbare Inserate nach Standort filtern und Verkäufer direkt kontaktieren.",
    faqTitle: "Häufig gestellte Fragen",
    faqs: [
      {
        q: "Wie kaufe ich einen gebrauchtwagen auf RO Auto Market?",
        a: "Nutzen Sie die Filter für Marke, Modell, Preis und Stadt, prüfen Sie Fotos und Historie und kontaktieren Sie den Verkäufer direkt. Für Käufer fällt keine Provision an.",
      },
      {
        q: "Woran erkenne ich einen seriösen Händler?",
        a: "Geprüfte Händler tragen eine eigene Kennzeichnung. Wir prüfen die Händlerfirma anhand von CUI-, CAEN-, RISCO- und ANAF-Daten, nicht den Zustand jedes Fahrzeugs.",
      },
      {
        q: "Kann ich als Privatperson verkaufen?",
        a: "Ja. Standard und Hervorgehoben sind 30 Tage aktiv. Bis zum Verkauf wird monatlich verlängert, bis das Inserat als verkauft markiert wird.",
      },
      {
        q: "Werden Importfahrzeuge aus Rumänien und Großbritannien gelistet?",
        a: "Ja. Einige Partnerhändler importieren geprüfte Fahrzeuge aus Rumänien und Großbritannien. Solche Inserate sind mit Herkunftsland und verfügbarer Wartungshistorie gekennzeichnet.",
      },
    ],
  },
};

const HomeSEOContent = ({ country }: Props) => {
  const c = CONTENT[country] || CONTENT.RO;

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: c.faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <section
      aria-labelledby="home-seo-heading"
      className="max-w-4xl mx-auto px-4 py-10"
    >
      <h2
        id="home-seo-heading"
        className="text-2xl font-bold text-foreground mb-4"
      >
        {c.h1}
      </h2>
      <div className="space-y-3 text-muted-foreground text-sm leading-relaxed">
        <p>{c.intro}</p>

        <h3 className="text-base font-semibold text-foreground pt-2">
          {c.howTitle}
        </h3>
        <ol className="list-decimal pl-5 space-y-2">
          {c.how.map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ol>

        <h3 className="text-base font-semibold text-foreground pt-2">
          {c.whyTitle}
        </h3>
        <p>{c.why} <Link to={country === "UK" ? "/uk/pricing" : country === "DE" ? "/de/pricing" : "/pricing"} className="text-primary underline underline-offset-2">{country === "UK" ? "See pricing" : country === "DE" ? "Preise ansehen" : "Vezi prețurile"}</Link></p>

        <h3 className="text-base font-semibold text-foreground pt-2">
          {c.coverageTitle}
        </h3>
        <p>{c.coverage}</p>

        <h3 className="text-base font-semibold text-foreground pt-2">
          {c.faqTitle}
        </h3>
        <dl className="space-y-3">
          {c.faqs.map((f) => (
            <div key={f.q}>
              <dt className="font-medium text-foreground">{f.q}</dt>
              <dd className="mt-1">{f.a}</dd>
            </div>
          ))}
        </dl>
      </div>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
    </section>
  );
};

export default memo(HomeSEOContent);
